import React from 'react';
import { ObjFaqItem, getScoreBadgeClasses } from '../../adherence/types';
import SummarySectionCard from './SummarySectionCard';

interface ObjectionHandlingSummaryCardProps {
  items: ObjFaqItem[];
  overallSummary?: string;
  onViewDetails: () => void;
  onViewInTimeline?: () => void;
}

const ObjectionHandlingSummaryCard: React.FC<ObjectionHandlingSummaryCardProps> = ({
  items,
  overallSummary,
  onViewDetails,
  onViewInTimeline,
}) => {
  const objectionItems = items.filter((item) => item.category === 'objection');
  const count = objectionItems.length;

  const avgScore =
    count > 0
      ? Math.round(objectionItems.reduce((sum, item) => sum + (item.handling_score ?? 0), 0) / count)
      : null;

  const worstItems = [...objectionItems]
    .sort((a, b) => (a.handling_score ?? 0) - (b.handling_score ?? 0))
    .slice(0, 3);

  // Find the lowest-scored item timestamp for timeline navigation
  const lowestItem = worstItems[0];
  const timelineTs = lowestItem?.timestamp_iso;

  const badgeColor = avgScore == null ? 'bg-gray-100 text-gray-500 border border-gray-200' : getScoreBadgeClasses(avgScore);

  const statusBadge =
    count === 0 ? (
      <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200">None</span>
    ) : avgScore != null ? (
      <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${badgeColor}`}>Avg {avgScore}%</span>
    ) : undefined;

  const coachingItems: string[] = worstItems
    .filter((item) => item.what_rep_should_have_said)
    .map((item) => `"${item.question?.slice(0, 60)}${item.question?.length > 60 ? '...' : ''}": ${item.what_rep_should_have_said}`);

  const resultContent = (
    <div className="space-y-2">
      {count === 0 ? (
        <p className="text-xs text-gray-500">No objections were detected in this call.</p>
      ) : (
        <>
          {overallSummary && <p className="text-xs text-gray-600">{overallSummary}</p>}
          <div className="flex items-center gap-3 mt-1">
            <div className="text-center">
              <p className="text-xl font-bold text-gray-700">{count}</p>
              <p className="text-[10px] text-gray-400">objection{count !== 1 ? 's' : ''}</p>
            </div>
            {avgScore != null && (
              <div className="text-center">
                <p className={`text-xl font-bold ${avgScore >= 70 ? 'text-green-600' : avgScore >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {avgScore}%
                </p>
                <p className="text-[10px] text-gray-400">avg handling</p>
              </div>
            )}
          </div>
          {worstItems.length > 0 && (
            <div className="mt-2 space-y-1.5">
              <p className="text-[10px] font-semibold text-gray-400 uppercase">Needs Improvement</p>
              {worstItems.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between bg-red-50 rounded px-2 py-1.5">
                  <span className="text-xs text-gray-700 truncate max-w-[70%]">{item.question}</span>
                  <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ml-2 flex-shrink-0 ${getScoreBadgeClasses(item.handling_score)}`}>
                    {item.handling_score}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <SummarySectionCard
      title="Objection Handling"
      icon={<span>&#128736;</span>}
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={coachingItems.length > 0 ? coachingItems : undefined}
      sectionKey="objection"
      onViewDetails={onViewDetails}
      onViewInTimeline={timelineTs ? onViewInTimeline : undefined}
    />
  );
};

export default ObjectionHandlingSummaryCard;
